import { segmentClient } from '@/util/analytics';

/**
 * Update tracking utilities for mobile app update events.
 *
 * Tracks OTA and store update flows: availability, prompts, actions, completion/failure,
 * phase transitions, and configuration loading. All events are native-only.
 */
export const UPDATE_EVENTS = {
  PROMPTED: { name: 'App update prompted', platforms: ['native'] as const },
  ACTION: { name: 'App update action', platforms: ['native'] as const },
  UPDATED: { name: 'App update completed', platforms: ['native'] as const },
  FAILED: { name: 'App update failed', platforms: ['native'] as const },
  PHASE: { name: 'App update phase', platforms: ['native'] as const },
  CONFIG: { name: 'App update config', platforms: ['native'] as const },
} as const;

/**
 * Action strings passed to `trackUpdateAction`. Centralized so Segment dashboards
 * and the testing strategy doc reference a single source of truth.
 */
export const UPDATE_ACTIONS = {
  BACKGROUND_DOWNLOAD: 'background_download',
  BANNER_DISMISSED: 'banner_dismissed',
  CONFIRM: 'confirm',
  DISMISS: 'dismiss',
  OPEN_STORE: 'open_store',
  OPEN_STORE_FALLBACK: 'open_store_fallback',
  RESTART_FROM_ACCOUNT: 'restart_from_account',
  STALE_RELOAD: 'stale_reload',
} as const;

export type UpdateAction = (typeof UPDATE_ACTIONS)[keyof typeof UPDATE_ACTIONS];

/**
 * Marks an update as completed with optional metadata.
 */
export const trackUpdateCompleted = (
  updateType: 'background' | 'ota' | 'store',
  currentVersion: string,
  userInitiated = false,
  isDefault = false,
  additionalProperties?: {
    username?: string;
    updateWasAlreadyDownloading?: boolean;
    updateWasAvailable?: boolean;
    updateFetchedSuccessfully?: boolean;
    updateDurationMs?: number;
    hadError?: boolean;
    required?: boolean;
  }
) =>
  segmentClient.track(UPDATE_EVENTS.UPDATED, {
    updateType,
    currentVersion,
    userInitiated,
    isDefault,
    ...additionalProperties,
  });

export const trackUpdateFailed = (
  updateType: 'background' | 'ota' | 'store',
  currentVersion: string,
  error: string,
  userInitiated = false,
  isDefault = false,
  additionalProperties?: {
    required?: boolean;
  }
) =>
  segmentClient.track(UPDATE_EVENTS.FAILED, {
    updateType,
    currentVersion,
    error,
    userInitiated,
    isDefault,
    ...additionalProperties,
  });

/**
 * When an update prompt is shown to the user.
 */
export function trackAppUpdatePrompted(properties: {
  updateType: 'ota' | 'store';
  required: boolean;
  currentVersion: string;
  isDownloading?: boolean;
  isUpdatePending?: boolean;
  latestVersion?: string;
}) {
  segmentClient.track(UPDATE_EVENTS.PROMPTED, properties);
}

/**
 * Captures update-related actions and background operations.
 */
export function trackUpdateAction(properties: {
  updateType: 'background' | 'ota' | 'store';
  action: UpdateAction;
  currentVersion: string;
  userInitiated?: boolean;
  isDefault?: boolean;
  additionalProperties?: {
    platform?: string;
    latestVersion?: string;
    required?: boolean;
    storeUrl?: string;
    fallbackUrl?: string;
    usedUrl?: string;
    primaryUrl?: string;
  };
}) {
  const { additionalProperties, ...baseProperties } = properties;

  segmentClient.track(UPDATE_EVENTS.ACTION, {
    ...baseProperties,
    ...additionalProperties,
  });
}

/**
 * Tracks phase transitions during the update process.
 */
export function trackUpdatePhase(properties: {
  updateType: 'ota' | 'store';
  currentVersion: string;
  required: boolean;
  phase: 'download' | 'reload';
  status: 'started';
}) {
  segmentClient.track(UPDATE_EVENTS.PHASE, properties);
}

/**
 * Tracks remote configuration status for update policies.
 */
export function trackAppConfig(properties: {
  status: 'loaded' | 'failed';
  hasServerConfig?: boolean;
  config?: {
    otaUpdate: { mode: string; required: boolean; message?: string };
    storeUpdate: { mode: string; required: boolean; message?: string; latestVersion?: string };
  };
  error?: string;
  fallbackConfig?: {
    otaUpdate: { mode: string; required: boolean };
    storeUpdate: { mode: string; required: boolean };
  };
}) {
  segmentClient.track(UPDATE_EVENTS.CONFIG, properties);
}
